import moment from 'moment';
import {
  ALL_DAYS_OF_WEEK,
  API_DAYS_OF_WEEK_ALL,
  WEEKDAY_DAYS,
  WEEKEND_DAYS,
  WEEKLY_SCHEDULE_TOGGLE
} from 'constants/daysOfWeek';
import { DaysOfWeek } from 'enums';
import { SHORT_DAY_NAME } from 'constants/general';
import { ActivityType } from 'interfaces/common.interface';
import { CustomRoutineType, SettingsType } from 'interfaces/setting.interface';
import {
  denormalizeActivityLogQuantityQuestionsForApi,
  normalizeActivityLogQuantityQuestions
} from 'utils/log-quantity-questions.util';
import { hasMeaningfulCutoff } from './validators';

const CANONICAL_DAY_TOKEN = new Set<string>(
  Object.values(DaysOfWeek) as string[]
);

function uniqueOrderedCanonicalDays(
  days: readonly (DaysOfWeek | string)[]
): DaysOfWeek[] {
  const out: DaysOfWeek[] = [];
  const seen = new Set<string>();
  for (const d of days) {
    const key = String(d).toUpperCase();
    if (!CANONICAL_DAY_TOKEN.has(key)) {
      continue;
    }
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(key as DaysOfWeek);
  }
  return out;
}

/**
 * Missing, empty, API `["ALL"]`, or unknown-only lists become {@link ALL_DAYS_OF_WEEK}.
 * Otherwise returns unique canonical MON–SUN tokens in first-seen order.
 */
export function normalizeDaysOfWeekValue(
  days: (DaysOfWeek | string)[] | undefined
): DaysOfWeek[] {
  if (days === undefined) {
    return [...ALL_DAYS_OF_WEEK];
  }
  if (days.length === 0) {
    return [...ALL_DAYS_OF_WEEK];
  }
  if (
    days.length === 1 &&
    String(days[0]).toUpperCase() === API_DAYS_OF_WEEK_ALL
  ) {
    return [...ALL_DAYS_OF_WEEK];
  }
  const unique = uniqueOrderedCanonicalDays(days);
  if (unique.length === 0) {
    return [...ALL_DAYS_OF_WEEK];
  }
  return unique;
}

export function normalizeActivityDaysOfWeek(
  activity: ActivityType
): ActivityType {
  return normalizeActivityLogQuantityQuestions({
    ...activity,
    cutoff_time_for_doing_activity:
      activity.cutoff_time_for_doing_activity ?? undefined, // Normalize API null cutoff values to undefined so app state uses a single "unset" shape.
    isLateCutoffEnabled: hasMeaningfulCutoff(
      activity.cutoff_time_for_doing_activity
    ),
    days_of_week: normalizeDaysOfWeekValue(activity.days_of_week)
  });
}

function mapActivities(
  activities: ActivityType[] | undefined
): ActivityType[] | undefined {
  if (activities === undefined) {
    return undefined;
  }
  return activities.map(normalizeActivityDaysOfWeek);
}

function mapActivitiesForApi(
  activities: ActivityType[] | undefined
): ActivityType[] | undefined {
  if (activities === undefined) {
    return undefined;
  }
  return activities.map((activity) =>
    denormalizeActivityLogQuantityQuestionsForApi({
      ...activity,
      days_of_week: normalizeDaysOfWeekValue(activity.days_of_week)
    })
  );
}

/** Applies `normalizeDaysOfWeekValue` to every habit list and scheduled routine in settings. */
export function normalizeSettingsType(settings: SettingsType): SettingsType {
  return {
    ...settings,
    morning_activities: mapActivities(settings.morning_activities),
    break_activities: mapActivities(settings.break_activities),
    evening_activities: mapActivities(settings.evening_activities),
    standalone_activities: mapActivities(settings.standalone_activities),
    custom_routines: settings.custom_routines?.map((routine) => ({
      ...routine,
      days_of_week: normalizeDaysOfWeekValue(
        routine.days_of_week as DaysOfWeek[]
      ) as unknown as string[],
      standalone_activities: mapActivities(routine.standalone_activities) ?? []
    }))
  };
}

/** Converts unset log bounds back to API 0/0 before persisting settings. */
export function denormalizeSettingsTypeForApi(
  settings: SettingsType
): SettingsType {
  return {
    ...settings,
    morning_activities: mapActivitiesForApi(settings.morning_activities),
    break_activities: mapActivitiesForApi(settings.break_activities),
    evening_activities: mapActivitiesForApi(settings.evening_activities),
    standalone_activities: mapActivitiesForApi(settings.standalone_activities),
    custom_routines: settings.custom_routines?.map((routine) => ({
      ...routine,
      days_of_week: normalizeDaysOfWeekValue(
        routine.days_of_week as DaysOfWeek[]
      ) as unknown as string[],
      standalone_activities:
        mapActivitiesForApi(routine.standalone_activities) ?? []
    }))
  };
}

export function normalizeCustomRoutineType(
  routine: CustomRoutineType
): CustomRoutineType {
  return {
    ...routine,
    days_of_week: normalizeDaysOfWeekValue(
      routine.days_of_week as DaysOfWeek[]
    ) as unknown as string[],
    standalone_activities: mapActivities(routine.standalone_activities) ?? []
  };
}

export function moreOptionsDaysOfWeekIsInvalid(
  days: DaysOfWeek[] | undefined
): boolean {
  return days === undefined || days.length === 0;
}

/** Compares day lists as sets so order does not affect equality (e.g. dirty checks). */
export function areDaysOfWeekSetsEqual(
  a?: (DaysOfWeek | string)[],
  b?: (DaysOfWeek | string)[]
): boolean {
  const toSet = (days?: (DaysOfWeek | string)[]) =>
    new Set(
      (days ?? [])
        .map((d) => String(d).toUpperCase())
        .filter((token) => CANONICAL_DAY_TOKEN.has(token))
    );

  const setA = toSet(a);
  const setB = toSet(b);
  if (setA.size !== setB.size) {
    return false;
  }
  for (const day of setA) {
    if (!setB.has(day)) {
      return false;
    }
  }
  return true;
}

export type WeeklyScheduleToggle =
  | DaysOfWeek
  | (typeof WEEKLY_SCHEDULE_TOGGLE)[keyof typeof WEEKLY_SCHEDULE_TOGGLE];

/** Selection after a weekly-schedule control click (single day, weekdays preset, or weekend preset). */
export function computeDaysOfWeekAfterWeeklyToggle(
  currentSelected: DaysOfWeek[],
  toggle: WeeklyScheduleToggle
): DaysOfWeek[] {
  if (toggle === WEEKLY_SCHEDULE_TOGGLE.WEEKDAYS) {
    return [...WEEKDAY_DAYS];
  }
  if (toggle === WEEKLY_SCHEDULE_TOGGLE.WEEKEND) {
    return [...WEEKEND_DAYS];
  }
  const next = [...currentSelected];
  const dayIndex = next.indexOf(toggle);
  if (dayIndex > -1) {
    next.splice(dayIndex, 1);
  } else {
    next.push(toggle);
  }
  return next;
}

/**
 * Activities shown on the routine-player route for **today** only.
 * Uses normalized `days_of_week` and includes the activity iff today's token is in that list.
 * Do not treat “has Saturday” as matching Sunday — that incorrectly showed Sat-only habits on Sun.
 */
export const getTodayActivities = (activities: ActivityType[]) => {
  const today = moment().locale('en').format(SHORT_DAY_NAME).toUpperCase();
  const key = today as DaysOfWeek;

  return activities.filter((activity) => {
    const days = normalizeDaysOfWeekValue(activity.days_of_week);
    return days.includes(key);
  });
};
